/**
 * Team Work, as the app has it: describe the work, the team's operations manager plans it,
 * teammates implement, the operations manager reviews. Questions from agents and tool approvals
 * wait above the composer, as the app's decision cards do in its timeline.
 */

import { BranchesRight, CircleCheck, Route } from '@gravity-ui/icons';
import { useState } from 'react';

import { ListGroup } from '../../components/ui.tsx';
import type { Decision, Team, Workbench } from '../useWorkbench.ts';
import { Conversation, type Sent } from './Conversation.tsx';
import { ApprovalCard, DecisionCard } from './shared.tsx';
import { Picker, type Suggestion, useBaseBranch, useComposer, WorkComposer, WorkHero, WorkStrip } from './work.tsx';

const SUGGESTIONS: Suggestion[] = [
  { tone: 'teal', icon: <Route />, text: 'Plan and deliver the next roadmap item' },
  { tone: 'violet', icon: <CircleCheck />, text: 'Add unit tests for the modules with the least coverage' },
  { tone: 'amber', icon: <BranchesRight />, text: 'Refactor duplicated logic into shared helpers' },
];

export function TeamWorkView({ w }: { w: Workbench }) {
  const branch = useBaseBranch(w);
  const teams = w.useView<Team[]>('teams.list', undefined, [], 20_000);
  const all = teams.data ?? [];
  const [picked, setPicked] = useState<string>();
  const team = all.find((t) => t.id === picked) ?? all.find((t) => t.running) ?? all[0];
  const decisions = w.useView<Decision[]>('decisions.list', { teamId: team?.id ?? 'solo' }, [team?.id], 8_000);
  const agents = w.activity.data?.agents ?? [];
  const ops = agents.find((a) => a.teamId === team?.id && a.isOpsManager) ?? agents.find((a) => a.teamId === team?.id);
  const open = (decisions.data ?? []).filter((d) => d.status !== 'answered');
  const approvals = agents.filter((a) => a.teamId === team?.id).flatMap((a) => a.permissions);
  const c = useComposer();
  const [sent, setSent] = useState<Sent[]>([]);
  const reload = () => {
    w.activity.reload();
    decisions.reload();
  };

  async function send() {
    if (!team) return;
    c.setBusy(true);
    const text = c.text.trim();
    await w.act('Send', async () => {
      const viaFacilitator = await w.tryCall<{ conversationId: string }>('facilitator.send', { mode: 'ops', teamId: team.id, text });
      if (!viaFacilitator) {
        if (!ops) throw new Error(`${team.name} is not running; start it first`);
        const r = await w.call<{ outcome: string }>('sessions.prompt', { sessionId: ops.sessionId, text, source: 'aioe-remote' });
        setSent((s) => [...s, { at: Date.now(), text, outcome: r?.outcome }]);
      } else {
        setSent((s) => [...s, { at: Date.now(), text, outcome: 'sent' }]);
      }
      c.setText('');
    });
    c.setBusy(false);
  }

  const waiting = open.length + approvals.length;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <WorkStrip label="Team Work" session={sent.length ? `${team?.name ?? ''} session` : 'new session'} branch={branch} />
      <div className="min-h-0 flex-1 overflow-auto">
        {sent.length ? (
          <Conversation w={w} sessionId={ops?.sessionId} sent={sent} />
        ) : (
          <div className="flex min-h-full flex-col">
            <WorkHero tone="blue" title="Team Work" blurb="Describe the work — the operations manager plans, workers implement, the operations manager reviews." suggestions={SUGGESTIONS} onPick={c.setText} />
          </div>
        )}
        {waiting > 0 && (
          <div className="mx-auto w-full max-w-4xl px-6 pb-4">
            <ListGroup title="Waiting on you" count={waiting} description="Questions from agents and tool calls to approve.">
              {open.map((d) => (
                <DecisionCard key={d.id} w={w} d={d} onDone={reload} />
              ))}
              {approvals.map((p) => (
                <ApprovalCard key={p.permissionId} w={w} p={p} onDone={reload} />
              ))}
            </ListGroup>
          </div>
        )}
      </div>
      <WorkComposer
        text={c.text}
        setText={c.setText}
        busy={c.busy}
        canSend={w.connected && !!team && (!!ops || !!team.running)}
        onSend={() => void send()}
        placeholder="Describe the work — the operations manager plans, workers build, operations manager reviews..."
        lands={
          <>
            Team work merges into <span className="mono font-medium text-foreground">{branch}</span>
          </>
        }
        pickers={
          all.length ? (
            <>
              <Picker label="Team" value={team?.id ?? ''} onChange={setPicked} options={all.map((t) => ({ value: t.id, label: t.name, hint: t.running ? undefined : 'stopped' }))} />
              {team && !team.running && (
                <button type="button" className="text-xs text-accent hover:underline" onClick={() => void w.act('Start team', () => w.call('teams.start', { teamId: team.id }), () => { teams.reload(); w.activity.reload(); })}>
                  Start {team.name}
                </button>
              )}
            </>
          ) : (
            <span className="text-xs text-muted">{w.connected ? 'No teams defined; build one in the app.' : 'Not connected.'}</span>
          )
        }
        footnote={team ? `Runs on "${team.name}" — its operations manager${ops?.isOpsManager ? ` (${ops.name})` : ''} plans, teammates implement, the operations manager reviews.` : ''}
      />
    </div>
  );
}
